import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { ICustomPushApiKeyCreateResponse } from '../../types/customPush';

interface ApiKeyCreatedDialogProps {
  open: boolean;
  onClose: () => void;
  result: ICustomPushApiKeyCreateResponse | null;
}

const ApiKeyCreatedDialog: React.FC<ApiKeyCreatedDialogProps> = ({ open, onClose, result }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy API key:', error);
    }
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  if (!result) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>API Key Created</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Copy this key now — for security it is shown only once and cannot be retrieved again.
        </Alert>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {result.name}
        </Typography>
        <TextField
          fullWidth
          value={result.apiKey}
          InputProps={{
            readOnly: true,
            sx: { fontFamily: 'monospace' },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleCopy} edge="end" color={copied ? 'success' : 'default'}>
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Send this key as the <code>x-api-key</code> header when calling the custom push notification endpoint.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApiKeyCreatedDialog;
